import fs from 'fs';

import { Config, fetchSTRAPI } from '@/utils/fetch';

export type StrapiCityPage = {
  id: number;
  attributes: {
    city_description: CityDescription[];
    page_subtitle: string;
    city: { data: StrapiCityDatum };
    createdAt: string;
    featured_destination_cities: { data: StrapiCityDatum[] };
    featured_origin_cities: { data: StrapiCityDatum[] };
    isVisibleInListView: boolean;
    publishedAt: string;
    updatedAt: string;
    versionComment: string;
    versionNumber: number;
    vuid: string;
  };
};

export type CityDescription = {
  children: { text: string; type: string }[];
  type: string;
};

export type MarketingImage = {
  attributes: ImageAttributes;
  id: number;
};

export type ImageAttributes = {
  alternativeText: null | string;
  caption: null | string;
  createdAt: string;
  ext: string;
  formats: {
    large: ImageFormat;
    medium: ImageFormat;
    small: ImageFormat;
    thumbnail: ImageFormat;
  };
  hash: string;
  height: number;
  mime: string;
  name: string;
  previewUrl: null | string;
  provider: string;
  provider_metadata: null | string;
  size: number;
  updatedAt: string;
  url: string;
  width: number;
};

export type ImageFormat = {
  ext: string;
  hash: string;
  height: number;
  mime: string;
  name: string;
  path: null | string;
  size: number;
  url: string;
  width: number;
};

export type FeaturedCities = FeaturedCityData[];

export type FeaturedCityData = {
  attributes: FeaturedCityAttributes;
  id: number;
};

export type FeaturedCityAttributes = {
  city_gson: null | string;
  city_id: string;
  city_name: string;
  city_slug: string;
  createdAt: string;
  locale: string;
  publishedAt: string;
  updatedAt: string;
};

export type StrapiReview = {
  id: number;
  attributes: {
    approved: boolean;
    carrier_name?: { data: { attributes: { carrier_id: string; carrier_name: string } } };
    review_score?: number;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    locale: string;
    review_text?: string;
    reviewer?: string;
    travel_date?: string;
    purchase_site?: string;
    round_trip: boolean;
    city_route?: StrapiCityRoute;
  };
};

export interface CarrierData {
  id: number;
  attributes: {
    aggregate_rating: number | null;
    carrier_address1: string | null;
    carrier_address2: string | null;
    carrier_city: string | null;
    carrier_contact_email: string | null;
    carrier_contact_phone: string | null;
    carrier_enabled: boolean;
    carrier_homepage: string | null;
    carrier_id: string;
    carrier_name: string;
    carrier_postal_code: string | null;
    carrier_privacy_url: string | null;
    carrier_slug: string;
    carrier_state: string | null;
    carrier_terms_url: string | null;
    carrier_type: string;
    createdAt: string;
    locale: string;
    loyalty_enabled: boolean;
    publishedAt: string;
    updatedAt: string;
  };
}

export type StrapiCityRoute = {
  data: StrapiCityRouteDatum;
};

export type StrapiCityRouteDatum = {
  attributes: {
    min_rating?: number | null | undefined;
    review_count?: number | null | undefined;
    rating_count?: number | null | undefined;
    max_rating?: number | null | undefined;
    origin_city?: { data: StrapiCityDatum };
    destination_city?: { data: StrapiCityDatum };
    aggregate_rating?: number;
    city_route_id: string;
    reviews?: {
      data: StrapiReview[];
    };
    carrier_direct_filter: { data: CarrierData[] };
    carrier_interline_filter: { data: CarrierData[] };
    origin_city_routes?: {
      data: StrapiCityRouteDatum[];
    };
    destination_city_routes?: {
      data: StrapiCityRouteDatum[];
    };
  };
  id: number;
};

export type StrapiPartnerLocationKey = {
  attributes: {
    partner_primary_key: string;
  };
  id: number;
};

export type StrapiPartnerLocationKeyResponse = {
  data: StrapiPartnerLocationKey[];
};
export type StrapiStops = {
  data: StrapiStop[];
};

export type StrapiCountry = {
  data: StrapiCountryDatum;
};

export type StrapiCountries = {
  data: StrapiCountryDatum[];
  meta: StrapiCountriesMeta;
};

export type StrapiCountryDatum = {
  attributes: StrapiCountryAttributes;
  id: number;
};

export type StrapiCountryAttributes = {
  country_id: string;
  country_name: string;
  states?: StrapiStates;
};

export type StrapiStates = {
  data: StrapiStateDatum[];
};

export type StrapiState = {
  data: StrapiStateDatum;
};

export type StrapiStateDatum = {
  attributes: StrapiStateDatumAttributes;
  id: number;
};

export type StrapiStateDatumAttributes = {
  cities?: StrapiCities;
  state_id: string;
  state_name: string;
  country?: StrapiCountry;
  hero_desktop?: StrapiMediaCollection;
  hero_tablet?: StrapiMediaCollection;
  hero_mobile?: StrapiMediaCollection;
};
export type StrapiCity = {
  data: StrapiCityDatum;
};

export type StrapiCities = {
  data: StrapiCityDatum[];
};

export type StrapiCityDatum = {
  id: number;
  attributes: StrapiCityDatumAttributes;
};

export type Synonym = {
  city_name_synonym: string;
};

export type StrapiCityDatumAttributes = {
  city_description_short?: CityDescription[];
  marketing_images?: {
    data: MarketingImage[];
  };
  stop_slug?: string;
  stop_name?: string;
  city_description?: string;
  city_id?: string;
  city_name?: string;
  city_slug: string;
  state?: StrapiState;
  stops?: StrapiStops;
  synonym?: Synonym[];
  nearby_stops?: StrapiStops;
  origin_city_routes?: { data: StrapiCityRouteDatum[] };
  destination_city_routes?: { data: StrapiCityRouteDatum[] };
  hero_desktop?: StrapiMediaCollection;
  hero_tablet?: StrapiMediaCollection;
  hero_mobile?: StrapiMediaCollection;
};

export type StrapiCityDataMock = {
  id: number;
  attributes: {
    slug: string;
    title: string;
    domain: string;
    description: {
      type: string;
      children: {
        text: string;
        type: string;
      }[];
    }[];
    hero_desktop: any;
    hero_tablet: any;
    hero_mobile: any;
    trip_groups: any;
  };
};

export type StrapiMediaCollection = {
  data: StrapiMediaDatum[];
};
export type StrapiMediaDatum = {
  id: number;
  attributes: StrapiMediaDatumAttributes;
};
export type StrapiMediaDatumAttributes = {
  name: string;
  alternativeText: string;
  caption: string;
  width: number;
  height: number;
  formats: StrapiMediaFormats;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string;
  provider: string;
  provider_metadata: string;
  createdAt: string;
  updatedAt: string;
};
export type StrapiMediaFormats = {
  large: StrapiMediaFormat;
  small: StrapiMediaFormat;
  medium: StrapiMediaFormat;
  thumbnail: StrapiMediaFormat;
};
export type StrapiMediaFormat = {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path: string;
  size: number;
  width: number;
  height: number;
};
export type StrapiCountriesMeta = {
  pagination: StrapiCountriesMetaPagination;
};

export type StrapiCountriesMetaPagination = {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
};

export type Amenity = {
  attributes: {
    amenity_name: string;
    amenity_type: string;
    createdAt: string;
    publishedAt: string;
    updatedAt: string;
  };
};

export type AmenitiesData = {
  data: Amenity[] | [];
};

export type StrapiStop = {
  attributes: {
    amenities?: AmenitiesData;
    stop_boarding_instructions?: string | null;
    Hours?: StrapiStopHour[] | undefined;
    cities?: StrapiCities;
    createdAt: string;
    locale: string;
    location_hours?: StrapiStopHour[] | undefined;
    partner_location_keys?: StrapiPartnerLocationKeyResponse;
    payment_forms?: any;
    phone_primary?: string;
    phone_secondary?: string;
    phone_tertiary?: string;
    publishedAt: string;
    stop_address1?: string;
    stop_address2?: string;
    stop_advanced_notice?: boolean;
    stop_destination?: boolean;
    stop_enabled?: boolean;
    stop_id: string;
    stop_latitude?: string;
    stop_longitude?: string;
    stop_name?: string;
    stop_origin?: boolean;
    stop_postal_code?: string;
    stop_show_open_24x7?: boolean;
    stop_slug?: string;
    stop_source?: string;
    stop_time_zone?: string;
    stop_type: string;
    stop_will_call?: boolean;
    updatedAt: string;
    commuter_stop?: boolean;
  };
  id: number;
};

export type StrapiCarrier = {
  attributes: {
    aggregate_rating?: number;
    carrier_address1?: string | null;
    carrier_address2?: string | null;
    carrier_city?: string | null;
    carrier_contact_email?: string | null;
    carrier_contact_phone?: string | null;
    carrier_enabled: boolean;
    carrier_homepage?: string | null;
    carrier_id: string;
    carrier_logo_rectangle?: { data: StrapiMediaDatum[] };
    carrier_name: string;
    carrier_postal_code?: string | null;
    carrier_privacy_url?: string | null;
    carrier_slug: string;
    carrier_state?: string | null;
    carrier_terms_url?: string | null;
    carrier_type: string;
    createdAt: string;
    group_with_carrier: { data: StrapiCarrier };
    locale: string;
    publishedAt: string;
    updatedAt: string;
    trailways_carrier?: boolean | null;
    review_count: number;
    rating_count: number;
    score_breakdown: any;
    reviews: { data: StrapiReview[] };
    loyalty_enabled: boolean;
  };
  id: number;
};

export type StrapiStopHour = {
  close_time?: string;
  day_interval?: string;
  id: number;
  note?: string;
  open_time?: string;
};

type FileImageFormat = {
  ext: string;
  url: string;
  hash: string;
  mime: string;
  name: string;
  path: string | null;
  size: number;
  width: number;
  height: number;
  sizeInBytes: number;
};

type FileAttributes = {
  alternativeText: string | null;
  caption: string | null;
  createdAt: string;
  ext: string;
  formats: {
    large?: FileImageFormat;
    medium?: FileImageFormat;
    small?: FileImageFormat;
    thumbnail?: FileImageFormat;
    [key: string]: FileImageFormat | undefined;
  } | null;
  hash: string;
  height: number | null;
  mime: string;
  name: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: string | null;
  size: number;
  updatedAt: string;
  url: string;
  width: number | null;
};

type FileData = {
  attributes: FileAttributes;
  id: number;
};

export type JobDescriptionText = {
  text: string;
  type: 'text' | 'list-item' | 'paragraph';
  bold?: boolean;
  italic?: boolean;
};

export type JobDescription = {
  type: 'paragraph' | 'list';
  children: JobDescriptionText[];
  format?: 'unordered' | 'ordered';
};

type JobLocation = {
  data: {
    attributes: {
      city_name: string;
      city_slug: string;
    };
    id: number;
  };
};

export type Carrier = {
  data: {
    attributes: {
      carrier_name: string;
    };
    id: number;
  };
};

export type JobPosting = {
  id: number;
  attributes: {
    application_pdf: { data: FileData[] | null };
    carrier: Carrier;
    createdAt: string;
    employment_type: string;
    job_full_description: JobDescription[];
    job_location: StrapiCity;
    job_short_description: string;
    job_title: string;
    job_type: string;
    locale: string;
    meta_description: string;
    page_title: string;
    publishedAt: string;
    salary_high: string;
    salary_low: string;
    salary_type: string;
    start_date: string;
    updatedAt: string;
    url_slug: string;
    job_image: { data: FileData[] | null };
  };
};

export type JobData = {
  data: JobPosting[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
};

export const getLocations = async (includeNearbyStops?: boolean): Promise<StrapiCountries> => {
  const locationsFile = './locations.json';
  if (process.env.STRAPI_LOCATIONS_CACHE === 'true') {
    if (fs.existsSync(locationsFile)) {
      return JSON.parse(fs.readFileSync(locationsFile).toString());
    }
  }
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 4 },
    path: 'countries',
    retry: true,
    params: {
      'fields[0]': 'country_id',
      'fields[1]': 'country_name',
      'populate[states][fields][0]': 'state_id',
      'populate[states][fields][1]': 'state_name',
      // We want city info, but not the geojson, as that's a lot of data
      // Strapi only supports this by listing what we do want
      'populate[states][populate][cities][fields][0]': 'city_name',
      'populate[states][populate][cities][fields][1]': 'city_slug',
      'populate[states][populate][cities][fields][2]': 'city_id',
      // Other
      'populate[states][populate][cities][populate][stops][fields][0]': 'stop_enabled',
      'populate[states][populate][cities][populate][stops][populate][partner_location_keys][fields][0]':
        'partner_location_active',
      'populate[states][populate][cities][populate][synonym]': 'true',
      'pagination[page]': '1',
      'pagination[pageSize]': '999',
      'pagination[withCount]': 'true'
    }
  };
  if (includeNearbyStops) {
    const nearbyStopsParams = {
      'populate[states][populate][cities][populate][nearby_stops][fields][0]': 'stop_enabled',
      'populate[states][populate][cities][populate][nearby_stops][populate][partner_location_keys][fields][0]':
        'partner_location_active'
    };

    config.params = { ...config.params, ...nearbyStopsParams };
  }
  const contents = await fetchSTRAPI(config);
  if (process.env.STRAPI_LOCATIONS_CACHE === 'true') {
    fs.writeFileSync(locationsFile, JSON.stringify(contents));
  }
  return contents;
};

// Used by front-end async calls
export const getLocationsData = async (includeNearbyStops?: boolean) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 },
    path: includeNearbyStops ? 'locations/nearby' : 'locations',
    retry: true
  };
  return await fetchSTRAPI(config);
};

export const getStops = async (params?: object) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 },
    path: 'stops',
    retry: true
  };
  if (params) {
    config.params = params;
  }
  return await fetchSTRAPI(config);
};

export const getAllStops = async () => {
  let page = 1;
  const stops = [];
  while (true) {
    const params = {
      'fields[0]': 'stop_slug',
      'fields[1]': 'updatedAt',
      'filters[stop_enabled][$eq]': true,
      'populate[cities][fields][0]': 'city_slug',
      'filters[stop_type][$not][$eq]': 'Non-Passenger Stop',
      'pagination[page]': page.toString(),
      'pagination[pageSize]': '100'
    };
    const data = await getStops(params);
    stops.push(...data.data);
    if (parseInt(data.meta.pagination.pageCount) === page) {
      break;
    }
    page += 1;
  }
  return stops;
};

export const getCities = async (params?: object) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 },
    path: 'cities',
    retry: true
  };
  if (params) {
    config.params = params;
  }
  return await fetchSTRAPI(config);
};

export const getCityRoutes = async (params?: object) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'city-routes'
  };
  if (params) {
    config.params = params;
  }
  return await fetchSTRAPI(config);
};

export const getReviewsAsync = async (cityPairId: string, page?: number, pageSize?: number) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'reviews',
    params: {
      'filters[city_route][city_route_id][$eq]': cityPairId,
      'filters[approved][$eq]': 'true',
      'fields[2]': 'review_text',
      'fields[3]': 'reviewer',
      'fields[1]': 'review_score',
      'fields[4]': 'travel_date',
      'fields[0]': 'publishedAt',
      'populate[carrier_name][fields][0]': 'carrier_id',
      'populate[carrier_name][fields][1]': 'carrier_name',
      '[sort][0]': 'publishedAt:desc',
      'pagination[pageSize]': pageSize,
      'pagination[page]': page
    },
    retry: true
  };
  return await fetchSTRAPI(config);
};

export const getReviews = async (cityPairId: string, page?: number, pageSize?: number) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 },
    path: 'reviews',
    retry: true,
    params: { cityPairId: cityPairId, page: page, pageSize: pageSize }
  };
  return await fetchSTRAPI(config);
};

export const getServiceAdvisories = async (params?: object) => {
  const config: Config = {
    method: 'GET',
    // Cache for longer in dev than prod
    // This is because we'd like to cache the homepage for as long as possible in dev
    // due to the amount of API calls it makes when it regens.
    // Since the service-advisories are on the homepage, they impact its cacheing
    next: { revalidate: process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? 2 * 3600 : 24 * 3600 },
    path: 'service-advisories'
  };
  return await fetchSTRAPI(config);
};

// Convenience functions
export const getStopBySlug = async (stop_slug: string): Promise<StrapiStop | null> => {
  const params = {
    'fields[0]': 'stop_name',
    'fields[1]': 'stop_slug',
    'fields[2]': 'stop_address1',
    'fields[3]': 'stop_address2',
    'fields[4]': 'stop_postal_code',
    'fields[5]': 'stop_time_zone',
    'fields[6]': 'stop_show_open_24x7',
    'fields[7]': 'stop_boarding_instructions',
    'fields[8]': 'stop_latitude',
    'fields[9]': 'stop_longitude',
    'fields[10]': 'stop_type',
    'fields[11]': 'stop_special_announcements',
    'fields[12]': 'phone_primary',
    'fields[13]': 'phone_secondary',
    'fields[14]': 'phone_tertiary',
    'fields[15]': 'stop_advanced_notice',
    'filters[stop_slug][$eq][0]': stop_slug,
    'filters[stop_enabled][$eq]': 'true',
    'filters[stop_type][$not][$eq]': 'Non-Passenger Stop',
    'populate': 'cities'
  };
  const stops = await getStops(params);
  if (stops && stops.data && stops.data.length) {
    return stops.data[0];
  }
  return null;
};

export const getStopByStopID = async (stop_id: string): Promise<StrapiStop | null> => {
  const params = {
    'filters[stop_id][$eq][0]': stop_id,
    'filters[stop_enabled][$eq]': 'true',
    'filters[stop_type][$not][$eq]': 'Non-Passenger Stop',
    'populate': 'cities'
  };
  const stops = await getStops(params);
  if (stops && stops.data && stops.data.length) {
    return stops.data[0];
  }
  return null;
};

export const getCityBySlug = async (
  city_state: string,
  includeStopDetails: boolean
): Promise<StrapiCityDatum | null> => {
  let params = {
    'fields[0]': 'city_id',
    'fields[1]': 'city_name',
    'fields[2]': 'city_slug',
    'fields[3]': 'city_description_short',
    'filters[city_slug][$eq]': city_state,
    'populate[marketing_images]': 'true',
    'populate[stops][filters][partner_location_keys][partner_primary_key][$notNull]': 'true', // Don't include stops without UUIDs
    'populate[stops][filters][stop_enabled][$eq]': 'true',
    'populate[stops][filters][stop_type][$not][$eq]': 'Non-Passenger Stop',
    'populate[stops][populate][partner_location_keys][fields][0]': 'partner_primary_key',
    'populate[stops][populate][cities][fields][0]': 'city_name',
    'populate[stops][populate][cities][populate][state][fields][0]': 'state_id',
    'populate[stops][populate][cities][populate][state][fields][1]': 'state_name',
    'populate[stops][fields][0]': 'stop_name',
    'populate[stops][fields][1]': 'stop_slug',
    'populate[stops][fields][2]': 'stop_address1',
    'populate[stops][fields][3]': 'stop_address2',
    'populate[stops][fields][4]': 'stop_postal_code',
    'populate[stops][fields][5]': 'stop_latitude',
    'populate[stops][fields][6]': 'stop_longitude',
    'populate[stops][fields][7]': 'stop_time_zone',
    'populate[stops][fields][8]': 'stop_type',
    'populate[stops][fields][9]': 'stop_advanced_notice',
    'populate[stops][fields][10]': 'stop_show_open_24x7',
    'populate[stops][fields][11]': 'phone_primary',
    'populate[stops][fields][12]': 'phone_secondary',
    'populate[stops][fields][13]': 'phone_tertiary',
    'populate[state][fields][0]': 'state_id',
    'populate[state][fields][1]': 'state_name',
    'populate[state][populate][country][fields][0]': 'country_id',
    'populate[state][populate][hero_desktop][fields][0]': 'name',
    'populate[state][populate][hero_desktop][fields][1]': 'url',
    'populate[state][populate][hero_tablet][fields][0]': 'name',
    'populate[state][populate][hero_tablet][fields][1]': 'url',
    'populate[state][populate][hero_mobile][fields][0]': 'name',
    'populate[state][populate][hero_mobile][fields][1]': 'url',
    'populate[hero_desktop][fields][0]': 'name',
    'populate[hero_desktop][fields][1]': 'url',
    'populate[hero_tablet][fields][0]': 'name',
    'populate[hero_tablet][fields][1]': 'url',
    'populate[hero_mobile][fields][0]': 'name',
    'populate[hero_mobile][fields][1]': 'url'
  };
  if (includeStopDetails) {
    const extraParams = {
      'populate[stops][fields][7]': 'stop_show_open_24x7',
      'populate[stops][fields][8]': 'stop_type',
      'populate[stops][fields][9]': 'phone_primary',
      'populate[stops][fields][10]': 'phone_secondary',
      'populate[stops][fields][11]': 'phone_tertiary',
      'populate[stops][fields][12]': 'stop_advanced_notice',

      'populate[nearby_stops][filters][stop_enabled][$eq]': 'true',
      'populate[nearby_stops][filters][stop_type][$not][$eq]': 'Non-Passenger Stop',
      'populate[nearby_stops][populate][cities][fields][0]': 'city_name',
      'populate[nearby_stops][populate][cities][populate][state][fields][0]': 'state_id',
      'populate[nearby_stops][fields][0]': 'stop_name',
      'populate[nearby_stops][fields][1]': 'stop_slug',
      'populate[nearby_stops][fields][2]': 'stop_address1',
      'populate[nearby_stops][fields][3]': 'stop_address2',
      'populate[nearby_stops][fields][4]': 'stop_postal_code',
      'populate[nearby_stops][fields][6]': 'stop_show_open_24x7',
      'populate[nearby_stops][fields][8]': 'stop_latitude',
      'populate[nearby_stops][fields][9]': 'stop_longitude',
      'populate[nearby_stops][fields][10]': 'stop_time_zone',
      'populate[nearby_stops][fields][11]': 'stop_type',
      'populate[nearby_stops][fields][12]': 'phone_primary',
      'populate[nearby_stops][fields][13]': 'phone_secondary',
      'populate[nearby_stops][fields][14]': 'phone_tertiary'
    };
    params = { ...params, ...extraParams };
  }

  const stops = await getCities(params);
  if (stops && stops.data && stops.data.length) {
    return stops.data[0];
  }
  return null;
};
/*
export const getRelatedRoutes = async (city_pair_id: string) => {

const params: any = {
  'filters[city_route_id][$eq]': city_pair_id,
  'populate[origin_city_routes][fields][origin_city][0]': 'city_slug',
  'populate[origin_city_routes][fields][destination_city][0]': 'city_slug',
  'populate[destination_city_routes][fields][origin_city][0]': 'city_slug',
  'populate[destination_city_routes][fields][destination_city][0]': 'city_slug',
  'populate[reviews][sort]': 'publishedAt:desc'
};
if (isFirstPage) {
  params['populate[reviews][limit]'] = 3;
  params['populate[reviews][start]'] = 0;
}
const routes = await getCityRoutes(params);
if (routes && routes.data && routes.data.length) {
  return routes.data[0];
}
return null;
}
*/

export const getCityRoute = async (city_pair_id: string, isFirstPage?: boolean) => {
  const params: any = {
    'fields[0]': 'aggregate_rating',
    'fields[1]': 'max_rating',
    'fields[2]': 'min_rating',
    'fields[3]': 'rating_count',
    'fields[4]': 'review_count',
    'filters[city_route_id][$eq]': city_pair_id,
    'populate[reviews][filters][approved]': 'true',
    'populate[reviews][fields][0]': 'publishedAt',
    'populate[reviews][fields][1]': 'review_score',
    'populate[reviews][fields][2]': 'review_text',
    'populate[reviews][fields][3]': 'reviewer',
    'populate[reviews][fields][4]': 'travel_date',
    'populate[carrier_direct_filter]': 'true',
    'populate[carrier_interline_filter]': 'true',
    'populate[origin_city_routes][fields][0]': 'destination_city',
    'populate[origin_city_routes][populate][destination_city][fields][0]': 'city_slug',
    'populate[origin_city_routes][populate][destination_city][fields][1]': 'city_name',
    'populate[origin_city_routes][limit]': 16,
    'populate[origin_city_routes][start]': 0,
    'populate[destination_city_routes][fields][0]': 'origin_city',
    'populate[destination_city_routes][populate][origin_city][fields][0]': 'city_slug',
    'populate[destination_city_routes][populate][origin_city][fields][1]': 'city_name',
    'populate[destination_city_routes][limit]': 16,
    'populate[destination_city_routes][start]': 0,
    'populate[reviews][sort]': 'publishedAt:desc'
  };
  if (isFirstPage) {
    params['populate[reviews][limit]'] = 3;
    params['populate[reviews][start]'] = 0;
  }
  const routes = await getCityRoutes(params);
  if (routes && routes.data && routes.data.length) {
    return routes.data[0];
  }
  return null;
};

export const getCarriers = async () => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'carriers',
    params: {
      'filters[carrier_enabled][$eq]': 'true',
      'fields[0]': 'carrier_id',
      'fields[1]': 'carrier_name',
      'fields[2]': 'carrier_terms_url',
      'fields[3]': 'carrier_slug',
      'fields[4]': 'aggregate_rating',
      'fields[5]': 'review_count',
      'fields[6]': 'rating_count',
      'fields[7]': 'score_breakdown',
      'fields[8]': 'trailways_carrier',
      'fields[9]': 'loyalty_enabled',
      'populate[carrier_logo_rectangle]': 'true',
      'populate[group_with_carrier][fields][0]': 'carrier_id',
      'populate[group_with_carrier][fields][1]': 'carrier_name',
      'populate[group_with_carrier][fields][2]': 'carrier_slug',
      'pagination[pageSize]': '100'
    },
    retry: true
  };
  return await fetchSTRAPI(config);
};

export const getCarrierReviews = async (carrierId: string, page?: number, pageSize?: number) => {
  if (!pageSize) {
    pageSize = 3;
  }
  if (!page) {
    page = 1;
  }
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'reviews',
    params: {
      'filters[carrier_name][carrier_id][$eq]': carrierId,
      'filters[approved][$eq]': 'true',
      'fields[0]': 'review_text',
      'fields[1]': 'reviewer',
      'fields[2]': 'review_score',
      '[sort][0]': 'createdAt:desc',
      'pagination[pageSize]': pageSize,
      'pagination[page]': page
    },
    retry: true
  };
  return await fetchSTRAPI(config);
};

// Used by front-end async calls
export const getCarrierReviewsAsync = async (carrierId: string, page?: number, pageSize?: number) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 },
    path: 'reviews',
    retry: true,
    params: { carrierId: carrierId, page: page, pageSize: pageSize }
  };
  return await fetchSTRAPI(config);
};

export const getCityPages = async (params?: object) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'destination-pages',
    retry: true
  };
  if (params) {
    config.params = params;
  }
  return await fetchSTRAPI(config);
};

export const getCityPageData = async (city_slug: string): Promise<StrapiCityPage | null> => {
  const params = {
    'fields[0]': 'city_description',
    'fields[1]': 'page_subtitle',
    'filters[city][city_slug][$eq]': city_slug,
    'populate[city][populate][marketing_images]': 'true',
    'populate[city][populate][state][populate][hero_desktop][fields][0]': 'name',
    'populate[city][populate][state][populate][hero_desktop][fields][1]': 'url',
    'populate[city][populate][state][populate][hero_tablet][fields][0]': 'name',
    'populate[city][populate][state][populate][hero_tablet][fields][1]': 'url',
    'populate[city][populate][state][populate][hero_mobile][fields][0]': 'name',
    'populate[city][populate][state][populate][hero_mobile][fields][1]': 'url',
    'populate[city][populate][hero_desktop][fields][0]': 'name',
    'populate[city][populate][hero_desktop][fields][1]': 'url',
    'populate[city][populate][hero_tablet][fields][0]': 'name',
    'populate[city][populate][hero_tablet][fields][1]': 'url',
    'populate[city][populate][hero_mobile][fields][0]': 'name',
    'populate[city][populate][hero_mobile][fields][1]': 'url',
    'populate[city][populate][state]': 'true',
    'populate[city][populate][stops][populate][partner_location_keys][fields][0]': 'partner_primary_key',
    'populate[featured_destination_cities][populate][marketing_images]': 'true',
    'populate[featured_destination_cities][populate][stops][populate][partner_location_keys][fields][0]':
      'partner_primary_key',
    'populate[featured_origin_cities][populate][marketing_images]': 'true',
    'populate[featured_origin_cities][populate][stops][populate][partner_location_keys][fields][0]':
      'partner_primary_key'
  };

  const cityPages = await getCityPages(params);
  if (cityPages && cityPages.data && cityPages.data.length) {
    //console.log(cityPages.data[0]);
    return cityPages.data[0];
  }
  return null;
};

export const getCommuterCities = async () => {
  const params = {
    'fields[0]': 'city_name',
    'fields[1]': 'city_slug',
    'populate[state][populate][country][fields][0]': 'country_id',
    'populate[state][populate][country][fields][1]': 'country_name',
    'populate[state][populate][cities][populate][synonym]': 'true',
    'populate[state][populate][cities][populate][stops][filters][partner_location_keys][partner_primary_key][$notNull]':
      'true',
    'populate[state][populate][cities][populate][stops][populate][partner_location_keys][fields][0]':
      'partner_primary_key',
    'populate[state][populate][cities][populate][stops][populate][partner_location_keys][fields][1]':
      'partner_location_active',
    'populate[state][fields][0]': 'state_id',
    'populate[state][fields][1]': 'state_name',
    'filters[stops][commuter_stop][$eq]': true
  };
  const stops = await getCities(params);
  if (stops && stops.data && stops.data.length) {
    return stops.data;
  }
  return null;
};

export const getJob = async (id: number, urlSlug: string) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'jobs',
    params: {
      'filters[id][$eq]': id,
      'filters[url_slug][$eq]': urlSlug,
      'populate[job_image]': 'true',
      'populate[job_location][populate][state][fields][0]': 'state_id',
      'pagination[pageSize]': '100'
    },
    retry: true
  };
  return await fetchSTRAPI(config);
};

export const getJobs = async () => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 3600 * 24 },
    path: 'jobs',
    params: {
      'populate[application_pdf]': 'true',
      'populate[job_image]': 'true',
      'populate[job_location][fields][0]': 'city_name',
      'populate[job_location][fields][1]': 'city_slug',
      'populate[job_location][populate][state][fields][0]': 'state_id',
      'populate[carrier][fields][0]': 'carrier_name',
      'pagination[pageSize]': '100'
    },
    retry: true
  };
  return await fetchSTRAPI(config);
};
